import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useBusiness } from "../business/provider";
import { DbBusinessClient, DateFields } from "../client/models";
import { DbBusinessMembership, DbMembershipType } from "../membership/models";
import { membershipService } from "../membership/service";
import { DbBusinessPaymentType } from "../business/models";
import { visitService } from "./service";
import { DbVisitType, WalkInCreateRequest } from "./models";
import {
  useWalkInState,
  WalkInProduct,
  WalkInService,
  WalkInStep,
  calculateSubtotal,
  calculateTax,
  calculateTotal,
  calculateRemainingAmount,
  calculateServiceMembershipDiscount,
  calculateTotalMembershipDiscount,
} from "./provider-state";
import { useRouter } from "next/router";
import { rewardSettingService } from "../reward-setting/service";

interface WalkInContextProps {
  selectedClient: DbBusinessClient | null;
  selectedDate: DateFields | null;
  services: WalkInService[];
  products: WalkInProduct[];
  currentStep: WalkInStep;
  setCurrentStep: (step: WalkInStep) => void;
  setServices: React.Dispatch<React.SetStateAction<WalkInService[]>>;
  setProducts: React.Dispatch<React.SetStateAction<WalkInProduct[]>>;
  availableMemberships: DbBusinessMembership[];
  showMembershipModal: boolean;
  setShowMembershipModal: (show: boolean) => void;
  selectedServiceForMembership: string | null;
  selectedPaymentMethod: DbBusinessPaymentType;
  paymentAmount: number;
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  additionalNote: string;
  setAdditionalNote: (value: string) => void;
  setSelectedClient: (client: DbBusinessClient | null) => void;
  setSelectedDate: (date: DateFields | null) => void;
  addService: () => void;
  removeService: (id: string | null) => void;
  updateService: (id: string | null, updates: Partial<WalkInService>) => void;
  addProduct: () => void;
  removeProduct: (id: string | null) => void;
  updateProduct: (id: string | null, updates: Partial<WalkInProduct>) => void;
  openMembershipModal: (serviceId: string | null) => void;
  selectServiceMembership: (membership: DbBusinessMembership | null) => void;
  setSelectedPaymentMethod: (method: DbBusinessPaymentType) => void;
  setPaymentAmount: (amount: number) => void;
  setPaymentAmountWithLimit: (amount: number) => void;
  externalPrepaidAmount: number;
  setExternalPrepaidAmount: (amount: number) => void;
  redeemedPoints: number;
  setRedeemedPoints: (points: number) => void;
  redeemedDiscount: number;
  setRedeemedDiscount: (discount: number) => void;
  generateBill: () => Promise<void>;
  resetWalkIn: () => void;

  calculateSubtotal: () => number;
  calculateTax: () => number;
  calculateTotal: () => number;
  calculateRemainingAmount: () => number;
  getAvailableMembershipsForService: (
    serviceId: string | null,
  ) => DbBusinessMembership[];
  calculateServiceMembershipDiscount: (service: WalkInService) => number;
  calculateTotalMembershipDiscount: () => number;
}

const WalkInContext = createContext<WalkInContextProps | null>(null);

const createTempId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function WalkInProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { selectedBusiness } = useBusiness();
  const router = useRouter();

  const state = useWalkInState();
  const {
    selectedClient,
    setSelectedClient,
    selectedDate,
    setSelectedDate,
    services,
    setServices,
    products,
    setProducts,
    currentStep,
    setCurrentStep,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentAmount,
    setPaymentAmount,
    showMembershipModal,
    setShowMembershipModal,
    selectedServiceForMembership,
    setSelectedServiceForMembership,
    isLoading,
    setIsLoading,
    error,
    setError,
    additionalNote,
    setAdditionalNote,
    resetState,
  } = state;
  const [externalPrepaidAmount, setExternalPrepaidAmount] = React.useState(0);
  const [redeemedPoints, setRedeemedPoints] = React.useState(0);
  const [redeemedDiscount, setRedeemedDiscount] = React.useState(0);
  const [availableMemberships, setAvailableMemberships] = React.useState<
    DbBusinessMembership[]
  >([]);

  useEffect(() => {
    const fetchMemberships = async () => {
      if (selectedClient && selectedBusiness) {
        try {
          const userMemberships =
            await membershipService.getUserActiveMembershipsByBusiness(
              selectedClient.id,
              selectedBusiness.id,
            );
          setAvailableMemberships(userMemberships);
        } catch (error) {
          console.error("Error fetching memberships:", error);
          setError("Failed to fetch memberships");
        }
      } else {
        setAvailableMemberships([]);
      }
    };

    fetchMemberships();
  }, [selectedClient, selectedBusiness]);

  const addService = () => {
    const newService: WalkInService = {
      id: createTempId(),
      service: null,
      calendar: null,
      price: 0,
      selectedMembership: null,
    };
    setServices((prev) => [...prev, newService]);
  };

  const removeService = (id: string | null) => {
    setServices((prev) => prev.filter((service) => service.id !== id));
  };

  const updateService = (
    id: string | null,
    updates: Partial<WalkInService>,
  ) => {
    setServices((prev) =>
      prev.map((service, index) => {
        const isTargetService =
          id === null ? service.id === null : service.id === id;

        if (isTargetService) {
          const newId =
            service.id === null ? Date.now().toString() : service.id;
          return { ...service, ...updates, id: newId };
        }
        return service;
      }),
    );
  };

  const addProduct = () => {
    const newProduct: WalkInProduct = {
      id: `${createTempId()}-p`,
      product: null,
      quantity: 0,
      price: 0,
    };
    setProducts((prev) => [...prev, newProduct]);
  };

  const removeProduct = (id: string | null) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const updateProduct = (
    id: string | null,
    updates: Partial<WalkInProduct>,
  ) => {
    setProducts((prev) =>
      prev.map((p, index) => {
        const isTargetProduct = id === null ? p.id === null : p.id === id;

        if (isTargetProduct) {
          const newId = p.id === null ? Date.now().toString() + "-p" : p.id;
          return { ...p, ...updates, id: newId };
        }
        return p;
      }),
    );
  };

  const openMembershipModal = (serviceId: string | null) => {
    if (!serviceId) return;
    setSelectedServiceForMembership(serviceId);
    setShowMembershipModal(true);
  };

  const selectServiceMembership = (membership: DbBusinessMembership | null) => {
    if (selectedServiceForMembership) {
      updateService(selectedServiceForMembership, {
        selectedMembership: membership,
      });
    }
    setShowMembershipModal(false);
    setSelectedServiceForMembership(null);
  };

  const getAvailableMembershipsForService = (serviceId: string | null) => {
    if (!serviceId) return [];
    const service = services.find((s) => s.id === serviceId);
    if (!service || !service.service) return [];

    return availableMemberships.filter((membership) => {
      if (membership.type === DbMembershipType.service) {
        const membershipService = membership.services?.find(
          (ms) => ms.service_id === service.service?.id,
        );
        return (
          membershipService &&
          membershipService.total_count - (membershipService.used_count || 0) >=
            1
        );
      } else if (membership.type === DbMembershipType.price) {
        const remainingBenefitAmount =
          (membership.benefit_amount || 0) -
          (membership.used_benefit_amount || 0);
        return remainingBenefitAmount >= service.price;
      }
      return false;
    });
  };

  const setPaymentAmountWithLimit = (amount: number) => {
    const total = calculateTotal(services, products);
    const totalToPay = Math.max(0, total - redeemedDiscount);
    const maxPayable = Math.max(0, totalToPay - externalPrepaidAmount);
    setPaymentAmount(Math.min(Math.max(amount, 0), maxPayable));
  };

  const calculateSubtotalWrapper = () => calculateSubtotal(services, products);
  const calculateTaxWrapper = () => calculateTax(products);
  const calculateTotalWrapper = () => calculateTotal(services, products);
  const calculateRemainingAmountWrapper = () =>
    calculateRemainingAmount(
      services,
      products,
      redeemedDiscount,
      externalPrepaidAmount,
      paymentAmount,
    );
  const calculateServiceMembershipDiscountWrapper = (service: WalkInService) =>
    calculateServiceMembershipDiscount(service);
  const calculateTotalMembershipDiscountWrapper = () =>
    calculateTotalMembershipDiscount(services);

  const generateBill = async () => {
    if (!selectedBusiness || !selectedClient || !selectedDate) return;

    setIsLoading(true);
    setError(null);

    try {
      // Call redeem API before creating visit if points are being redeemed
      let redemptionFailed = false;
      if (
        redeemedPoints &&
        redeemedDiscount &&
        redeemedPoints > 0 &&
        redeemedDiscount > 0
      ) {
        try {
          // Calculate original bill amount (before discount) for redeem API
          const originalBillAmount =
            calculateTotal(services, products) + redeemedDiscount;
          await rewardSettingService.redeemPoints(
            selectedBusiness.id,
            redeemedPoints,
            originalBillAmount,
            selectedClient.id,
          );
        } catch (_error) {
          // If redeem fails, continue with booking without discount
          redemptionFailed = true;
        }
      }
      const toIsoDate = () => {
        const now = new Date();
        // Create date with selected year/month/day and current time
        const bookingDate = new Date(
          selectedDate.year,
          selectedDate.month - 1,
          selectedDate.day,
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds(),
        );
        return bookingDate.toISOString();
      };

      const toYmd = () =>
        `${selectedDate.year}-${String(selectedDate.month).padStart(2, "0")}-${String(
          selectedDate.day,
        ).padStart(2, "0")}`;

      const mappedServices = services
        .filter((s) => s.service && s.calendar)
        .map((s) => {
          const duration = s.service?.service_duration || 0;
          const actualPrice = s.price || 0;

          const now = new Date();
          const currentTimeString = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

          return {
            business_id: selectedBusiness.id,
            user_id: selectedClient.id,
            entity_id: s.calendar!.id,
            service_id: s.service!.id,
            service_time: currentTimeString,
            booking_date: toYmd(),
            service_duration: duration,
            booking_amount: actualPrice,
            payment_membership_id: s.selectedMembership?.id,
          };
        });

      const mappedProducts = products
        .filter((p) => p.product)
        .map((p) => ({
          inventory_id: p.product!.inventory_id,
          id: String(p.product!.id),
          name: p.product!.product_name,
          amount: p.price || 0,
          quantity: p.quantity || 0,
          total_amount: (p.quantity || 0) * (p.price || 0),
        }));

      const payments: Array<{
        type: DbBusinessPaymentType;
        amount: number;
        payment_id?: string | null;
        reward_points?: number;
      }> = [];

      if (!redemptionFailed && redeemedDiscount > 0 && redeemedPoints > 0) {
        payments.push({
          type: DbBusinessPaymentType.RewardPoints,
          amount: redeemedDiscount,
          payment_id: null,
          reward_points: redeemedPoints,
        });
      }

      if ((paymentAmount || 0) > 0) {
        payments.push({
          type: selectedPaymentMethod as DbBusinessPaymentType,
          amount: paymentAmount,
          payment_id: null,
        });
      }

      const baseTotal = calculateTotal(services, products);

      // Calculate total paid amount (includes reward points discount + cash/UPI payment)
      const totalPaidAmount =
        (!redemptionFailed && redeemedDiscount > 0 ? redeemedDiscount : 0) +
        (paymentAmount || 0);

      const payload: WalkInCreateRequest = {
        business_id: selectedBusiness.id,
        user_id: selectedClient.id,
        type: DbVisitType.WalkIn,
        booking_date: toIsoDate(),
        services: mappedServices,
        products: mappedProducts,
        financial_summary: {
          tax_amount: calculateTax(products),
          total_amount: baseTotal,
          paid_amount: !redemptionFailed ? totalPaidAmount : paymentAmount || 0,
          membership_discount_total: calculateTotalMembershipDiscount(services),
        },
        payments,
        additional_note: additionalNote?.trim() || undefined,
      };

      await visitService.createVisit(payload);
      router.push("/walk-in");
    } catch (_error) {
      setError("Failed to generate bill. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetWalkIn = useCallback(() => {
    resetState();
    setAvailableMemberships([]);
    setRedeemedPoints(0);
    setRedeemedDiscount(0);
  }, [resetState]);

  return (
    <WalkInContext.Provider
      value={{
        selectedClient,
        selectedDate,
        services,
        products,
        currentStep,
        setCurrentStep,
        setServices,
        setProducts,
        availableMemberships,
        showMembershipModal,
        setShowMembershipModal,
        selectedServiceForMembership,
        selectedPaymentMethod,
        paymentAmount,
        isLoading,
        error,
        setError,
        additionalNote,
        setAdditionalNote,
        setSelectedClient,
        setSelectedDate,
        addService,
        removeService,
        updateService,
        addProduct,
        removeProduct,
        updateProduct,

        openMembershipModal,
        selectServiceMembership,
        setSelectedPaymentMethod,
        setPaymentAmount,
        setPaymentAmountWithLimit,
        externalPrepaidAmount,
        setExternalPrepaidAmount,
        redeemedPoints,
        setRedeemedPoints,
        redeemedDiscount,
        setRedeemedDiscount,
        generateBill,
        resetWalkIn,

        calculateSubtotal: calculateSubtotalWrapper,
        calculateTax: calculateTaxWrapper,
        calculateTotal: calculateTotalWrapper,
        calculateRemainingAmount: calculateRemainingAmountWrapper,
        getAvailableMembershipsForService,
        calculateServiceMembershipDiscount:
          calculateServiceMembershipDiscountWrapper,
        calculateTotalMembershipDiscount:
          calculateTotalMembershipDiscountWrapper,
      }}
    >
      {children}
    </WalkInContext.Provider>
  );
}

export const useWalkIn = () => {
  const context = useContext(WalkInContext);
  if (!context) {
    throw new Error("useWalkIn must be used within WalkInProvider");
  }
  return context;
};
